import React, { useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Link,
  Divider,
} from "@mui/material";
import { useWhitelabel } from "../context/WhitelabelContext";
import { getInstanceConfig, getLogoMode, getSupportContactInfo } from "../utils";
import { useNavigate } from "react-router-dom";
import BrandedPageFooter from "../components/BrandedPageFooter";

interface TermsOfServiceProps {
  mode: string;
}

const TermsOfService: React.FC<TermsOfServiceProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { whitelabelSettings, whitelabelLogos, loaded } = useWhitelabel();
  const instanceConfig = getInstanceConfig(mode, whitelabelSettings);

  // Check if a URL would cause a redirect loop by pointing back to our terms page
  const wouldCauseRedirectLoop = (url: string): boolean => {
    try {
      const targetUrl = new URL(url, window.location.origin);
      const currentHost = window.location.hostname.toLowerCase();
      const targetHost = targetUrl.hostname.toLowerCase();
      const targetPath = targetUrl.pathname.toLowerCase();

      // Check if the URL points to a terms page on the same domain
      if (targetHost === currentHost) {
        // Check for common terms page paths
        if (targetPath === '/terms' || targetPath === '/terms/' ||
            targetPath === '/terms-of-service' || targetPath === '/terms-of-service/' ||
            targetPath === '/tos' || targetPath === '/tos/') {
          return true;
        }
      }
      return false;
    } catch {
      // If URL parsing fails, don't redirect to be safe
      return true;
    }
  };

  // Redirect to external terms URL if configured (unless it would cause a loop)
  const termsUrl = whitelabelSettings?.termsUrl;
  const shouldRedirectToExternal = termsUrl && !wouldCauseRedirectLoop(termsUrl);

  useEffect(() => {
    if (!loaded) return;

    if (shouldRedirectToExternal) {
      window.location.href = termsUrl;
    }
  }, [loaded, shouldRedirectToExternal, termsUrl]);

  // Show blank page while loading or redirecting
  if (!loaded || shouldRedirectToExternal) {
    return <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }} />;
  }

  const getLogoUrl = () => {
    if (mode === "dark" && whitelabelLogos?.horizontal_logo_dark?.url) {
      return whitelabelLogos.horizontal_logo_dark.url;
    }
    if (whitelabelLogos?.horizontal_logo?.url) {
      return whitelabelLogos.horizontal_logo.url;
    }
    return getLogoMode(mode);
  };

  const appName = whitelabelSettings?.title || "GTM AI";
  const companyName = appName;
  const supportContact = getSupportContactInfo(whitelabelSettings);
  const legalEmail = whitelabelSettings?.securityEmail || whitelabelSettings?.ownerEmail || whitelabelSettings?.salesEmail || "legal@" + window.location.hostname;
  const effectiveDate = "December 11, 2025";
  const jurisdiction = "Delaware, United States";

  const Section: React.FC<{
    title: string;
    children: React.ReactNode;
  }> = ({ title, children }) => (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );

  const BulletList: React.FC<{ items: string[] }> = ({ items }) => (
    <Box component="ul" sx={{ pl: 3, my: 1 }}>
      {items.map((item, idx) => (
        <Typography
          key={idx}
          component="li"
          variant="body2"
          color="text.secondary"
          sx={{ mb: 0.5 }}
        >
          {item}
        </Typography>
      ))}
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: "background.default",
          borderBottom: 1,
          borderColor: "divider",
          py: 6,
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <Box
            component="img"
            src={getLogoUrl()}
            alt={appName}
            sx={{
              height: 48,
              mb: 3,
              objectFit: "contain",
              cursor: "pointer",
            }}
            onClick={() => navigate("/")}
          />
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{ color: instanceConfig.primaryColor }}
          >
            Terms of Service
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: "auto" }}>
            Please read these Terms of Service ("Terms", "Agreement") carefully before using
            the {appName} platform and services operated by {companyName}.
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            Effective Date: {effectiveDate}
          </Typography>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 6, flex: 1 }}>
        <Typography variant="body2" color="text.secondary" paragraph>
          <strong>Important:</strong> By accessing or using our services, you agree to be bound
          by these Terms. If you disagree with any part of these Terms, you may not access
          our services.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Section title="1. Acceptance of Terms">
          <Typography variant="body2" color="text.secondary" paragraph>
            By creating an account, accessing, or using {appName}'s services, website, applications,
            or any related services (collectively, the "Services"), you acknowledge that you have
            read, understood, and agree to be bound by these Terms of Service and our Privacy Policy.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            If you are using the Services on behalf of an organization, you represent and warrant
            that you have the authority to bind that organization to these Terms, and "you" and
            "your" will refer to both you individually and that organization.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We reserve the right to modify these Terms at any time. Material changes will be
            communicated via email or notice on our website. Continued use of the Services after
            changes constitutes acceptance of the modified Terms.
          </Typography>
        </Section>

        <Section title="2. Description of Services">
          <Typography variant="body2" color="text.secondary" paragraph>
            {appName} provides an AI-powered platform for businesses that includes, but is not
            limited to:
          </Typography>
          <BulletList
            items={[
              "Access to multiple AI language models for content generation and analysis",
              "Knowledge base integration and management",
              "AI agents for customer support, voice, SMS, and email automation",
              "Lead generation and prospecting tools",
              "Workflow automation and integration capabilities",
              "Content creation and marketing tools",
              "Analytics and reporting features",
            ]}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            The specific features available to you depend on your subscription plan. We reserve
            the right to modify, suspend, or discontinue any part of the Services at any time
            with reasonable notice.
          </Typography>
        </Section>

        <Section title="3. Account Registration and Security">
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Account Creation:
          </Typography>
          <BulletList
            items={[
              "You must provide accurate, current, and complete information during registration",
              "You must be at least 18 years old or the age of majority in your jurisdiction",
              "You may not use another person's account without permission",
              "Business accounts must be registered by authorized representatives",
            ]}
          />

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
            Account Security:
          </Typography>
          <BulletList
            items={[
              "You are responsible for maintaining the confidentiality of your login credentials",
              "You must notify us immediately of any unauthorized access to your account",
              "You are responsible for all activities that occur under your account",
              "We recommend enabling multi-factor authentication when available",
            ]}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            We reserve the right to suspend or terminate accounts that we reasonably believe
            violate these Terms or are being used fraudulently.
          </Typography>
        </Section>

        <Section title="4. Subscription Plans and Payment">
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Subscription Terms:
          </Typography>
          <BulletList
            items={[
              "Subscriptions are billed in advance on a monthly or annual basis",
              "Pricing and features are subject to change with reasonable notice",
              "Usage limits apply based on your subscription tier",
              "Overage charges may apply for usage exceeding plan limits",
            ]}
          />

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
            Payment:
          </Typography>
          <BulletList
            items={[
              "Payment is due at the start of each billing period",
              "All fees are non-refundable except as required by law or stated in our refund policy",
              "You authorize us to charge your payment method for all fees owed",
              "Failed payments may result in suspension of services",
            ]}
          />

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
            Free Trials and Credits:
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            We may offer free trials or promotional credits at our discretion. Trial periods
            automatically convert to paid subscriptions unless cancelled before the trial ends.
            Promotional credits have no cash value and expire as specified.
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Cancellation:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You may cancel your subscription at any time through your account settings. Upon
            cancellation, you will retain access until the end of your current billing period.
            No prorated refunds are provided for partial billing periods.
          </Typography>
        </Section>

        <Section title="5. Acceptable Use Policy">
          <Typography variant="body2" color="text.secondary" paragraph>
            You agree not to use the Services to:
          </Typography>
          <BulletList
            items={[
              "Violate any applicable laws, regulations, or third-party rights",
              "Generate, distribute, or store illegal, harmful, or objectionable content",
              "Engage in spam, phishing, or other deceptive practices",
              "Attempt to gain unauthorized access to our systems or other users' accounts",
              "Interfere with or disrupt the integrity or performance of the Services",
              "Reverse engineer, decompile, or attempt to extract the source code of the Services",
              "Use the Services to build a competitive product or service",
              "Exceed usage limits or attempt to circumvent technical limitations",
              "Generate content that infringes on intellectual property rights",
              "Create deepfakes or synthetic media for deception or harm",
              "Use AI features to generate misleading or fraudulent content",
              "Harass, abuse, or harm others using the Services",
            ]}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Violation of this Acceptable Use Policy may result in immediate suspension or
            termination of your account without refund.
          </Typography>
        </Section>

        <Section title="6. AI Services Terms">
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            AI Output:
          </Typography>
          <BulletList
            items={[
              "AI-generated content is provided 'as is' without guarantees of accuracy or suitability",
              "You are responsible for reviewing, editing, and verifying AI-generated content before use",
              "AI outputs may not be factually accurate or complete",
              "You must not represent AI-generated content as human-created when disclosure is required",
            ]}
          />

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
            Data Processing:
          </Typography>
          <BulletList
            items={[
              "Your data is processed by AI models to provide the Services",
              "We do not use your data to train AI models without explicit consent",
              "We implement zero data retention policies with AI providers where available",
              "AI providers are bound by data processing agreements",
            ]}
          />

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
            Third-Party AI Models:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Our Services may utilize third-party AI models (e.g., OpenAI, Anthropic, Google).
            Use of these models is subject to the respective providers' terms and acceptable
            use policies. We are not responsible for the behavior or outputs of third-party models.
          </Typography>
        </Section>

        <Section title="7. Data Handling and Regulatory Compliance">
          <Typography variant="body2" color="text.secondary" paragraph>
            Each party represents and warrants that it will comply with all applicable laws, rules,
            regulations, directives, and guidelines regarding the collection, use, and disclosure
            of data collected from or about end users or specific devices which apply to the Services
            utilized hereunder (collectively, the "Rules").
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Applicable Regulations:
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            The term "Rules" includes, without limitation:
          </Typography>
          <BulletList
            items={[
              "All United States Federal Trade Commission (FTC) rules and guidelines regarding the collection, use, and disclosure of data from or about end users and/or specific devices",
              "All United States federal and state laws regarding data collection and data privacy, including the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA)",
              "The Self-Regulatory Principles and guidance of the Interactive Advertising Bureau (IAB), Network Advertising Initiative (NAI), and Digital Advertising Alliance (DAA)",
              "If applicable, the European Union General Data Protection Regulation (EU) 2016/679 (GDPR) and all amendments and updates thereto",
              "Any other applicable jurisdiction-specific data protection and privacy laws",
            ]}
          />

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
            Your Obligations:
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            You represent and warrant that, where required by the Rules, you or the entity that
            collected any data (including device-level data) has obtained legally compliant consent
            to the collection, use, and disclosure of such information to allow {appName} to process
            such data in connection with the Services.
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Notification Requirements:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You shall promptly notify us in the event you become aware of any breach of your
            data handling obligations under this section, including any unauthorized access to,
            or disclosure of, personal data processed through the Services.
          </Typography>
        </Section>

        <Section title="8. Intellectual Property Rights">
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Our Intellectual Property:
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            The Services, including all software, designs, text, graphics, and other content
            provided by us, are owned by {companyName} or our licensors and are protected by
            intellectual property laws. You may not copy, modify, distribute, sell, or lease
            any part of our Services without our prior written consent.
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Your Content:
          </Typography>
          <BulletList
            items={[
              "You retain ownership of content you create, upload, or input into the Services",
              "You grant us a license to use your content to provide and improve the Services",
              "This license is limited, non-exclusive, and revocable upon account termination",
              "AI-generated outputs created using your inputs belong to you",
            ]}
          />

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
            Feedback:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Any feedback, suggestions, or ideas you provide about our Services become our property
            and may be used without restriction or compensation to you.
          </Typography>
        </Section>

        <Section title="9. Confidentiality">
          <Typography variant="body2" color="text.secondary" paragraph>
            Both parties agree to maintain the confidentiality of any confidential information
            disclosed during the course of this Agreement. Confidential information includes
            but is not limited to:
          </Typography>
          <BulletList
            items={[
              "Business strategies, plans, and financial information",
              "Customer data and personally identifiable information",
              "Technical information, source code, and trade secrets",
              "Non-public features or product roadmap information",
            ]}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This obligation survives termination of this Agreement for a period of three (3) years.
          </Typography>
        </Section>

        <Section title="10. Disclaimer of Warranties">
          <Typography variant="body2" color="text.secondary" paragraph sx={{ fontWeight: 600 }}>
            THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
            EITHER EXPRESS OR IMPLIED.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            To the fullest extent permitted by law, we disclaim all warranties, including but
            not limited to:
          </Typography>
          <BulletList
            items={[
              "Implied warranties of merchantability, fitness for a particular purpose, and non-infringement",
              "Warranties that the Services will be uninterrupted, secure, or error-free",
              "Warranties regarding the accuracy, reliability, or completeness of AI outputs",
              "Warranties regarding results obtained from using the Services",
            ]}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Some jurisdictions do not allow the exclusion of certain warranties, so some of
            the above exclusions may not apply to you.
          </Typography>
        </Section>

        <Section title="11. Limitation of Liability">
          <Typography variant="body2" color="text.secondary" paragraph sx={{ fontWeight: 600 }}>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, {companyName.toUpperCase()} SHALL NOT BE LIABLE
            FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            This includes, without limitation:
          </Typography>
          <BulletList
            items={[
              "Loss of profits, revenue, data, or business opportunities",
              "Damages arising from service interruptions or downtime",
              "Damages arising from unauthorized access to your data",
              "Damages arising from third-party actions or content",
              "Damages arising from AI-generated content or recommendations",
            ]}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            <strong>
              In no event shall our total liability exceed the greater of: (a) the amount you
              paid us in the twelve (12) months preceding the claim, or (b) one hundred dollars ($100).
            </strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            These limitations apply regardless of the theory of liability (contract, tort, or otherwise)
            and even if we have been advised of the possibility of such damages.
          </Typography>
        </Section>

        <Section title="12. Indemnification">
          <Typography variant="body2" color="text.secondary" paragraph>
            You agree to defend, indemnify, and hold harmless {companyName}, its affiliates,
            officers, directors, employees, and agents from and against any claims, damages,
            losses, liabilities, costs, and expenses (including reasonable attorneys' fees)
            arising from or related to:
          </Typography>
          <BulletList
            items={[
              "Your use of the Services",
              "Your violation of these Terms",
              "Your violation of any third-party rights",
              "Content you create, upload, or distribute through the Services",
              "Your use of AI-generated content",
            ]}
          />
        </Section>

        <Section title="13. Termination">
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Termination by You:
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            You may terminate your account at any time through your account settings or by
            contacting support. Upon termination, your right to use the Services will cease
            immediately.
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Termination by Us:
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            We may suspend or terminate your account at any time, with or without cause, with
            or without notice. Reasons for termination may include but are not limited to:
          </Typography>
          <BulletList
            items={[
              "Violation of these Terms or our Acceptable Use Policy",
              "Non-payment of fees",
              "Suspected fraudulent, abusive, or illegal activity",
              "Extended periods of inactivity",
              "Discontinuation of Services",
            ]}
          />

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
            Effect of Termination:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upon termination, you will lose access to your account and all data stored within
            the Services. You may request export of your data within 30 days of termination.
            Sections that by their nature should survive termination shall survive, including
            intellectual property provisions, disclaimers, indemnification, and limitations
            of liability.
          </Typography>
        </Section>

        <Section title="14. Dispute Resolution">
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Governing Law:
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            These Terms shall be governed by and construed in accordance with the laws of
            {jurisdiction}, without regard to its conflict of law provisions.
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Informal Resolution:
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Before initiating formal dispute resolution, you agree to contact us at{" "}
            <Link href={`mailto:${legalEmail}`}>{legalEmail}</Link> to attempt to resolve
            the dispute informally within 30 days.
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Arbitration:
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Any dispute not resolved informally shall be resolved through binding arbitration
            in accordance with the rules of the American Arbitration Association. The arbitration
            shall be conducted in {jurisdiction}. Each party shall bear its own costs, and the
            decision of the arbitrator shall be final and binding.
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Class Action Waiver:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You agree that any disputes shall be conducted in your individual capacity only and
            not as a class action or other representative proceeding. You expressly waive any
            right to participate in class actions or class-wide arbitration.
          </Typography>
        </Section>

        <Section title="15. International Use">
          <Typography variant="body2" color="text.secondary" paragraph>
            The Services are operated from the United States. If you access the Services from
            outside the United States, you are responsible for compliance with local laws.
            You agree not to use the Services in any jurisdiction where such use would be
            illegal or otherwise prohibited.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We make no representation that the Services are appropriate or available for use
            in any particular location. By accessing the Services, you consent to the transfer
            of your data to the United States and other jurisdictions as described in our
            Privacy Policy.
          </Typography>
        </Section>

        <Section title="16. European Union (EU) Users">
          <Typography variant="body2" color="text.secondary" paragraph>
            If you are a European Union consumer, you will benefit from any mandatory provisions
            of the law of the country in which you are resident.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Additionally, if you are located in the European Economic Area (EEA), you have certain
            rights under the General Data Protection Regulation (GDPR). Please refer to our Privacy
            Policy for more information about how we process your personal data and your rights
            under GDPR.
          </Typography>
        </Section>

        <Section title="17. United States Legal Compliance">
          <Typography variant="body2" color="text.secondary" paragraph>
            You represent and warrant that:
          </Typography>
          <BulletList
            items={[
              "You are not located in a country that is subject to the United States government embargo, or that has been designated by the United States government as a 'terrorist supporting' country",
              "You are not listed on any United States government list of prohibited or restricted parties",
              "You will comply with all applicable U.S. export control laws and regulations",
            ]}
          />
        </Section>

        <Section title="18. Changes to Terms">
          <Typography variant="body2" color="text.secondary" paragraph>
            We reserve the right to modify these Terms at any time. We will provide notice of
            material changes by:
          </Typography>
          <BulletList
            items={[
              "Posting the updated Terms on our website",
              "Updating the 'Effective Date' at the top of this document",
              "Sending email notification to the address associated with your account",
              "Displaying a notice within the Services",
            ]}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Your continued use of the Services after the effective date of the revised Terms
            constitutes your acceptance of the changes. If you do not agree to the modified
            Terms, you must stop using the Services and terminate your account.
          </Typography>
        </Section>

        <Section title="19. General Provisions">
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Entire Agreement:
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            These Terms, together with our Privacy Policy and any other agreements referenced
            herein, constitute the entire agreement between you and {companyName} regarding
            the Services and supersede all prior agreements.
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Severability:
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            If any provision of these Terms is found to be unenforceable, the remaining
            provisions will continue in full force and effect.
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Waiver:
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Our failure to enforce any right or provision of these Terms shall not constitute
            a waiver of such right or provision.
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Assignment:
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            You may not assign or transfer these Terms without our prior written consent. We
            may assign these Terms without restriction.
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Force Majeure:
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Neither party shall be liable for any failure or delay in performance due to causes
            beyond its reasonable control, including but not limited to acts of God, natural
            disasters, war, terrorism, riots, government actions, or internet outages.
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Translation Interpretation:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            These Terms may have been translated if we have made them available to you on our
            Service. You agree that the original English text shall prevail in the case of a dispute.
          </Typography>
        </Section>

        <Section title="20. Contact Information">
          <Typography variant="body2" color="text.secondary" paragraph>
            If you have any questions about these Terms of Service, please contact us:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Legal Inquiries:</strong>{" "}
            <Link href={`mailto:${legalEmail}`}>{legalEmail}</Link>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            For general support inquiries, please visit our{" "}
            <Link
              href={supportContact}
              target={supportContact.startsWith("mailto:") ? undefined : "_blank"}
              rel={supportContact.startsWith("mailto:") ? undefined : "noopener noreferrer"}
            >
              support page
            </Link>
            .
          </Typography>
        </Section>
      </Container>

      <BrandedPageFooter currentPage="terms" />
    </Box>
  );
};

export default TermsOfService;
